/**
 * One AudioContext for the app: listening, the simulated microphone and playback share it, so
 * only one audio thread runs (and an echo canceller, where one is used, sees the app's own output). Created lazily
 * (browsers allow starting it only after a user gesture).
 */
let shared: AudioContext | undefined;

export function audioContext(): AudioContext {
  if (!shared || shared.state === 'closed') shared = new AudioContext();
  return shared;
}

interface AudioSession {
  type: string;
}

/**
 * Declares the page a media player (Audio Session API, Safari on iOS 17+, feature-detected): by
 * default iOS treats Web Audio as "ambient" sound, which the ring/silent switch mutes, so the
 * briefing was silent on a phone set to silent (QA 2026-10-12). Left alone once the microphone has
 * declared play-and-record.
 */
function declarePlayback() {
  const session = (navigator as Navigator & { audioSession?: AudioSession }).audioSession;
  try {
    if (session?.type === 'auto') session.type = 'playback';
  } catch {
    // not supported in this browser
  }
}

/** A tiny silent WAV, played by a media element: older iOS then plays Web Audio as media too. */
let silentElement: HTMLAudioElement | undefined;
function silentWav(): string {
  const samples = 800;
  const bytes = new DataView(new ArrayBuffer(44 + samples * 2));
  const text = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) bytes.setUint8(at + i, s.charCodeAt(i));
  };
  text(0, 'RIFF');
  bytes.setUint32(4, 36 + samples * 2, true);
  text(8, 'WAVEfmt ');
  bytes.setUint32(16, 16, true);
  bytes.setUint16(20, 1, true);
  bytes.setUint16(22, 1, true);
  bytes.setUint32(24, 8000, true);
  bytes.setUint32(28, 16000, true);
  bytes.setUint16(32, 2, true);
  bytes.setUint16(34, 16, true);
  text(36, 'data');
  bytes.setUint32(40, samples * 2, true);
  return URL.createObjectURL(new Blob([bytes.buffer], { type: 'audio/wav' }));
}

let speechUnlocked = false;

/**
 * Call synchronously inside a tap (before any await): iOS lets audio and speech start only from a
 * tap itself, and later playback builds on this. Declares media playback, resumes the shared
 * context and plays a silent buffer through it, and speaks an empty utterance (unlocks speech
 * synthesis). Harmless elsewhere; installed for every tap by `unlockAudioOnTaps`.
 */
export function unlockAudio(): void {
  declarePlayback();
  if (typeof AudioContext !== 'undefined') {
    const context = audioContext();
    if (context.state !== 'running') {
      void context.resume();
      const source = context.createBufferSource();
      source.buffer = context.createBuffer(1, 1, context.sampleRate);
      source.connect(context.destination);
      source.start();
      if (typeof Audio !== 'undefined' && !silentElement) {
        silentElement = new Audio(silentWav());
        silentElement.setAttribute('playsinline', '');
        void silentElement.play().catch(() => undefined);
      }
    }
  }
  if (!speechUnlocked && 'speechSynthesis' in window && !window.speechSynthesis.speaking) {
    speechUnlocked = true;
    const silent = new SpeechSynthesisUtterance('');
    silent.volume = 0;
    window.speechSynthesis.speak(silent);
  }
}

/**
 * Unlocks audio on every tap anywhere in the app, so any screen's voice can play (the briefing
 * started its voice after loading the sheets, outside the tap, and iOS kept it silent; QA
 * 2026-10-12). Cheap once the context runs.
 */
export function unlockAudioOnTaps(): void {
  const unlock = () => {
    unlockAudio();
  };
  for (const type of ['pointerdown', 'touchend', 'click', 'keydown'])
    window.addEventListener(type, unlock, { capture: true, passive: true });
}
